import { Link } from "react-router-dom";
import { Check, Phone, ShieldCheck } from "lucide-react";
import SEO from "../components/SEO";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { PHONE_DISPLAY, PHONE_FIXE_DISPLAY, PHONE_FIXE_TEL, PHONE_TEL, SERVICES } from "../data/site";

const TABLES = [
    {
        title: "Tarif Ouverture de porte Paris",
        head: ["Ouverture de porte", "Tarif", "Total"],
        rows: [
            ["Porte simple claquée", "60€", "60,00€"],
            ["Porte simple fermée à clef", "60€", "60€"],
            ["Porte blindée claquée", "60€", "60€"],
            ["Changement de serrure", "85€", "sur devis*"],
        ],
        foot: "* Obtenez un devis gratuit ou appelez-nous.",
    },
    {
        title: "Tarif Changement de serrure Paris",
        head: ["Changement serrure", "Tarif", "Total"],
        rows: [
            ["Serrure simple", "85€", "sur devis*"],
            ["Remplacement de cylindre", "85€", "sur devis*"],
            ["Serrure multipoint", "135€", "sur devis*"],
            ["Réglage de porte", "135€", "sur devis*"],
        ],
        foot: "* Prix de la serrure à définir — obtenez un devis gratuit.",
    },
    {
        title: "Tarif Double Clef Paris",
        head: ["Double Clef", "Tarif", "Total"],
        rows: [
            ["Clef plate", "5€", "5€"],
            ["Clef à gorges", "11€", "11€"],
            ["Clef spéciale", "à partir de 35€", "à partir de 35€"],
        ],
        foot: "Marques : Point Fort Fichet — Bricard — Picard — Vak — Heracles — Securystar — Reelax — Pollux — Medeco — Dom — Service Expert Vachette.",
    },
    {
        title: "Blindage de porte, Porte blindée Paris",
        head: ["Porte blindée", "À partir de", "Total"],
        rows: [
            ["Blindage de porte BP1", "sur devis", "sur devis*"],
            ["Blindage de porte BP3", "sur devis", "sur devis*"],
            ["Porte blindée BP1", "sur devis", "sur devis*"],
            ["Porte blindée BP3", "sur devis", "sur devis*"],
        ],
        foot: "* Obtenez un devis gratuit.",
    },
];

const COMMITMENTS = [
    "Devis gratuit et sans engagement avant chaque intervention",
    "Devis écrit obligatoire — et systématique — au-delà de 150€ de travaux",
    "Prix TTC annoncés au téléphone, aucune majoration cachée",
    "Facture détaillée : main d'œuvre et pièces clairement séparées",
    "Tarifs conformes aux exigences des compagnies d'assurance",
    "En urgence : devis/facture remis sur place avant paiement",
];

const FACTEURS_PRIX = [
    {
        facteur: "Type d'intervention",
        detail: "Une porte simplement claquée (pêne demi-tour non engagé) coûte 60 € TTC, quelle que soit l'heure. Une porte fermée à clé ou une serrure haute sécurité demande plus de temps et peut nécessiter un cylindre de remplacement — le tarif est établi sur devis.",
    },
    {
        facteur: "Marque et niveau du cylindre",
        detail: "Un cylindre d'entrée de gamme coûte entre 30 et 60 €. Un cylindre A2P BP1 se négocie entre 80 et 120 €. Les cylindres haute sécurité A2P BP2/BP3 (Fichet, Abloy, Evva) peuvent atteindre 150 à 250 €. La main-d'œuvre de pose reste identique.",
    },
    {
        facteur: "Heure d'intervention",
        detail: "La plupart des prestations ont le même tarif de jour comme de nuit. Un supplément de 20 à 30 € peut s'appliquer entre 22h et 7h du matin. Ce supplément est toujours annoncé au téléphone avant le déplacement.",
    },
    {
        facteur: "Complexité de la serrure",
        detail: "Une serrure simple à 3 points coûte moins cher qu'une serrure 7 points multipoints. Un diagnostic sur place permet d'affiner l'estimation. Le devis est gratuit et sans engagement.",
    },
];

const FAQ_TARIFS = [
    {
        q: "Pourquoi le prix varie-t-il selon le serrurier ?",
        a: "Les prix varient selon les coûts de structure (un artisan local a moins de frais qu'une centrale nationale), la qualité des pièces posées (cylindres certifiés A2P vs marque inconnue), et les pratiques commerciales. Certains opérateurs affichent un prix d'appel très bas qui ne comprend pas la main-d'œuvre réelle. Exigez toujours un prix TTC complet avant d'accepter l'intervention.",
    },
    {
        q: "La TVA est-elle incluse dans vos prix ?",
        a: "Oui, tous nos tarifs sont affichés TTC (toutes taxes comprises, TVA à 20 %). Aucune TVA ne sera ajoutée sur place. La facture que vous recevez détaille la base HT, le taux de TVA appliqué et le total TTC.",
    },
    {
        q: "Y a-t-il des frais de déplacement ?",
        a: "Non, nos frais de déplacement sont inclus dans le tarif de la prestation pour Paris intra-muros et la petite couronne (92, 93, 94). Pour la grande couronne (77, 78, 91, 95), un forfait déplacement peut s'appliquer — il vous est annoncé au téléphone avant tout déplacement.",
    },
    {
        q: "Mon assurance peut-elle prendre en charge la prestation ?",
        a: "Oui, si votre contrat multirisques habitation comprend une garantie « assistance serrurerie » ou « dépannage ». Nous émettons une facture conforme (SIRET, TVA, détail pièces + main-d'œuvre) que vous transmettez à votre assureur. Certains contrats remboursent jusqu'à 300 € par sinistre — vérifiez vos conditions générales.",
    },
];

export default function Tarifs() {
    return (
        <div data-testid="tarifs-page">
            <SEO
                title="Tarifs Serrurier Paris — Ouverture de Porte 60€ TTC | Prix Transparents"
                description="Grille tarifaire serrurier Paris : ouverture de porte 60€ TTC, changement de serrure 85€, serrure multipoint 135€, double de clef dès 5€, coffre-fort 150€. Devis gratuit, prix TTC sans surprise. ☎ 01 84 21 07 67"
                path="/tarifs"
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">Tarifs</span>
                    </nav>
                    <SectionLabel dark>Transparence tarifaire</SectionLabel>
                    <StatusBadge className="mt-4" />
                    <h1 className="mt-3 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["Nos tarifs.", "Sans surprise."]} />
                    </h1>
                    <FadeUp delay={0.5}>
                        <p className="mt-5 max-w-xl leading-relaxed text-white/70">
                            Des prix clairs, annoncés avant l'intervention et consignés sur un devis gratuit.
                            Le prix annoncé est le prix payé — c'est notre engagement.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* GRILLE SERVICES */}
            <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {SERVICES.map((s, i) => (
                        <FadeUp key={s.title} delay={(i % 3) * 0.08}>
                            <div
                                data-testid={`price-card-${i}`}
                                className="group flex h-full flex-col justify-between border border-midnight/10 bg-white p-8 transition-all duration-300 hover:-translate-y-2 hover:border-midnight hover:shadow-[0_28px_60px_-20px_rgba(10,17,40,0.35)]"
                            >
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-midnight/40">
                                        {String(i + 1).padStart(2, "0")} — Serrurerie
                                    </p>
                                    <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight">{s.title}</h2>
                                    <p className="mt-6 font-display text-4xl font-extrabold tracking-tight text-[#f97316]">
                                        {s.price}
                                    </p>
                                    <p className="mt-4 text-sm leading-relaxed text-midnight/60">{s.desc}</p>
                                </div>
                                <a
                                    href={PHONE_TEL}
                                    data-testid={`price-card-call-${i}`}
                                    className="mt-8 flex items-center justify-center gap-2 border border-midnight/15 py-3.5 text-sm font-bold text-midnight transition-colors duration-300 group-hover:border-emergency group-hover:bg-emergency group-hover:text-white"
                                >
                                    <Phone className="h-4 w-4" /> Appeler pour ce service
                                </a>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* 4 TABLEAUX OFFICIELS */}
            <section className="bg-white py-14 md:py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Grille officielle</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Tarifs détaillés par prestation
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-6 lg:grid-cols-2">
                        {TABLES.map((t, ti) => (
                            <FadeUp key={t.title} delay={(ti % 2) * 0.08}>
                                <div className="overflow-hidden rounded-xl border border-midnight/10 bg-white shadow-[0_24px_60px_-28px_rgba(10,17,40,0.2)]" data-testid={`tarif-table-${ti}`}>
                                    <h3 className="bg-midnight px-6 py-5 text-center font-display text-xl font-extrabold tracking-tight text-white md:text-2xl">
                                        {t.title}
                                    </h3>
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="border-b border-midnight/10">
                                                <th className="px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-midnight/50">{t.head[0]}</th>
                                                <th className="px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-midnight/50">{t.head[1]}</th>
                                                <th className="px-6 py-3.5 text-right text-xs font-bold uppercase tracking-wider text-midnight/50">{t.head[2]}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-midnight/10">
                                            {t.rows.map((r) => (
                                                <tr key={r[0]} className="transition-colors hover:bg-cloud">
                                                    <td className="px-6 py-4 font-semibold">{r[0]}</td>
                                                    <td className="px-6 py-4 font-display text-lg font-extrabold text-[#f97316]">{r[1]}</td>
                                                    <td className="px-6 py-4 text-right font-semibold text-midnight/70">{r[2]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <p className="px-6 py-4 text-xs leading-relaxed text-midnight/50">{t.foot}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                    <p className="mt-6 text-sm text-midnight/50" data-testid="tarifs-note">
                        * Prix de la serrure à définir selon le modèle — obtenez un{" "}
                        <Link to="/devis-contact" className="font-semibold text-emergency underline">devis gratuit</Link> ou
                        appelez le <a href={PHONE_FIXE_TEL} className="font-semibold text-emergency underline">{PHONE_FIXE_DISPLAY}</a>.
                    </p>
                </div>
            </section>

            {/* FACTEURS & FAQ TARIFS */}
            <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
                <FadeUp>
                    <SectionLabel>Comprendre le prix</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        Ce qui influence le tarif d'un serrurier
                    </h2>
                    <p className="mt-6 max-w-3xl leading-relaxed text-midnight/70">
                        Un même problème peut avoir des coûts très différents selon la situation. Voici les
                        quatre variables qui déterminent le prix final — expliquées clairement, sans jargon.
                    </p>
                </FadeUp>
                <div className="mt-10 grid gap-5 sm:grid-cols-2">
                    {FACTEURS_PRIX.map((f, i) => (
                        <FadeUp key={f.facteur} delay={i * 0.07}>
                            <div className="h-full border border-midnight/10 bg-white p-8">
                                <span className="font-display text-4xl font-extrabold text-midnight/10">{String(i + 1).padStart(2, "0")}</span>
                                <h3 className="mt-2 font-display text-xl font-extrabold tracking-tight">{f.facteur}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-midnight/70">{f.detail}</p>
                            </div>
                        </FadeUp>
                    ))}
                </div>

                <FadeUp delay={0.3}>
                    <h2 className="mt-16 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        FAQ — Tarifs serrurier Paris
                    </h2>
                    <div className="mt-8 space-y-4">
                        {FAQ_TARIFS.map((item, i) => (
                            <FadeUp key={item.q} delay={i * 0.06}>
                                <details className="group border border-midnight/10 bg-white">
                                    <summary className="flex cursor-pointer items-center justify-between px-7 py-5 font-display text-lg font-bold tracking-tight">
                                        {item.q}
                                        <Check className="h-4 w-4 shrink-0 text-emergency" />
                                    </summary>
                                    <div className="px-7 pb-6 pt-1 leading-relaxed text-midnight/70">{item.a}</div>
                                </details>
                            </FadeUp>
                        ))}
                    </div>
                </FadeUp>
            </section>


            {/* ENGAGEMENTS */}
            <section className="grain bg-midnight py-14 text-white md:py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel dark>Nos engagements</SectionLabel>
                        <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            L'engagement transparence Serrurier Paris
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
                        {COMMITMENTS.map((c, i) => (
                            <FadeUp key={c} delay={(i % 3) * 0.08} className="bg-midnight">
                                <div className="flex h-full items-start gap-4 p-7" data-testid={`commitment-${i}`}>
                                    <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-gold" strokeWidth={1.8} />
                                    <p className="text-sm leading-relaxed text-white/75">{c}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                    <FadeUp delay={0.15}>
                        <div className="mt-12 flex flex-wrap items-center gap-4">
                            <a
                                href={PHONE_TEL}
                                data-testid="tarifs-call-button"
                                className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                            >
                                <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                            </a>
                            <Link
                                to="/devis-contact"
                                data-testid="tarifs-devis-button"
                                className="flex items-center gap-2 border border-white/30 px-8 py-4 font-bold text-white transition-colors hover:border-gold hover:bg-gold hover:text-midnight"
                            >
                                <Check className="h-4 w-4" /> Demander un devis gratuit
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>
        </div>
    );
}
